import { Module } from 'vuex';
import BoardService from '@/services/board-service';
import { JOBS_BOARD_CONFIG } from '@/config/board-config';
import UXProjectsService from "@/services/ux/uxprojects";
import UXTechnicianService from "@/services/ux/uxtechnicians";
import UXEmployeeService from "@/services/ux/uxemployee";
import UXDueDateService from "@/services/ux/uxdates";
import MODULE from "@/constants/modules";
import GlobalServices from "@/services/global";

const global = new GlobalServices();

// Board state interface
export interface BoardState {
  isLoading: boolean;
  boardColumns: BoardColumn[];
  filters: BoardFilters;
  filterOptions: FilterOptions;
  lastRefresh: Date | null;
  selectedJob: BoardJob | null;
  totalJobs: number;
  boardService: BoardService | null;
  // Client-side filtering state
  clientSideJobFilter: string;
}

export interface BoardColumn {
  id: string;
  title: string;
  subTitle?: string;
  borderClass: string;
  list: BoardJob[];
  order: number;
  statusIds?: number[]; // dynamic configuration of included status IDs
}

export interface BoardJob {
  id: string;
  intProjectID: number;
  intProjectStatusID?: number; // Project status ID for updates
  client: string;
  location: string;
  total: string;
  modifiedDate: Date;
  status: string;
  technicianStatus?: string; // added technician status
  badgeStatusClass: string;
  borderClass: string;
  category: string;
  priority: string;
  assignee: string;
  type: string;
  dueDate: string;
  displayDueDate: string;
  displayDueDateStatus: string;
  createdBy: string;
  primaryContactName: string;
  primaryContactPhone: string;
  htmlViewStatus: string;
  htmlColourStatus: string;
  htmlColourTechnicianStatus?: string; // added technician color
  htmlViewDueDate: string;
  viewDetailsUrl: string;
  projectName: string;
  jobDescription: string;
  displayCreatedDate: string;
  time: string;
}

export interface BoardFilters {
  client: string | number;
  clientObject: any | null; // Store the full client object for display
  jobNumber: string;
  jobType: string | number;
  jobTypeObject: any | null; // Store the full job type object for display
  assignee: string | number;
  assigneeObject: any | null; // Store the full assignee object for display
  address: string;
  dueDate: string | number;
  dueDateObject: any | null; // Store the full due date object for display
}

export interface FilterOptions {
  clients: any[];
  jobTypes: any[];
  assignees: any[];
  dueDates: any[];
  projectStatuses: any[]; // Add project statuses to filter options
}

const boardModule: Module<BoardState, any> = {
  namespaced: true,
  
  state: (): BoardState => ({
    isLoading: true,
    boardColumns: [],
    filters: {
      client: "",
      clientObject: null,
      jobNumber: "",
      jobType: "",
      jobTypeObject: null,
      assignee: "",
      assigneeObject: null,
      address: "",
      dueDate: "",
      dueDateObject: null,
    },
    filterOptions: {
      clients: [],
      jobTypes: [],
      assignees: [],
      dueDates: [],
      projectStatuses: [],
    },
    lastRefresh: null,
    selectedJob: null,
    totalJobs: 0,
    boardService: null,
    clientSideJobFilter: '',
  }),

  mutations: {
    SET_LOADING(state, isLoading: boolean) {
      state.isLoading = isLoading;
    },

    SET_BOARD_COLUMNS(state, columns: BoardColumn[]) {
      state.boardColumns = columns;
      // Clear client-side filter when new data arrives from API
      if (!state.filters.jobNumber) {
        state.clientSideJobFilter = '';
      }
    },

    SET_TOTAL_JOBS(state, total: number) {
      state.totalJobs = total;
    },

    SET_BOARD_SERVICE(state, service: BoardService) {
      state.boardService = service;
    },

    SET_FILTERS(state, filters: BoardFilters) {
      state.filters = { ...state.filters, ...filters };
    },

    CLEAR_FILTERS(state) {
      state.filters = {
        client: "",
        clientObject: null,
        jobNumber: "",
        jobType: "",
        jobTypeObject: null,
        assignee: "",
        assigneeObject: null,
        address: "",
        dueDate: "",
        dueDateObject: null,
      };
      state.clientSideJobFilter = '';
    },

    SET_FILTER_OPTIONS(state, options: Partial<FilterOptions>) {
      state.filterOptions = { ...state.filterOptions, ...options };
    },

    SET_SELECTED_JOB(state, job: BoardJob | null) {
      state.selectedJob = job;
    },

    UPDATE_LAST_REFRESH(state) {
      state.lastRefresh = new Date();
    },

    UPDATE_JOB_STATUS(state, { jobId, newStatus, newStatusId, newColumnId }: { jobId: string, newStatus: string, newStatusId?: number, newColumnId: string }) {
      // Find and remove job from current column
      let jobToMove: BoardJob | null = null;
      for (const column of state.boardColumns) {
        const jobIndex = column.list.findIndex(job => job.id === jobId);
        if (jobIndex !== -1) {
          jobToMove = column.list.splice(jobIndex, 1)[0];
          break;
        }
      }

      // Add job to new column with updated status
      if (jobToMove) {
        jobToMove.status = newStatus;
        // Update status ID if provided
        if (newStatusId) {
          jobToMove.intProjectStatusID = newStatusId;
        }
        const targetColumn = state.boardColumns.find(col => col.id === newColumnId);
        if (targetColumn) {
          targetColumn.list.push(jobToMove);
        }
      }
    },

    ANIMATE_JOB_MOVE(state, { jobId, fromColumnId, toColumnId }: { jobId: string, fromColumnId: string, toColumnId: string }) {
      // This mutation can be used to trigger animations
      // The actual animation would be handled in the component
      // Animation triggered for job move
    },

    SET_CLIENT_SIDE_JOB_FILTER(state, filter: string) {
      state.clientSideJobFilter = filter;
    }
  },

  actions: {
    async initializeBoardData({ dispatch }) {
      await dispatch('initializeBoard');
    },

    async initializeBoard({ commit, dispatch, rootGetters }) {
      try {
        commit('SET_LOADING', true);
        
        // Ensure board settings are loaded first
        if (!rootGetters['boardSettings/isLoaded']) {
          await dispatch('boardSettings/initializeBoardSettings', null, { root: true });
        }

        // Initialize the board service with status data
        const boardService = new BoardService();

        // Fetch status list to initialize the board service
        try {
          const GlobalServices = (await import('@/services/global')).default;
          const MODULE = (await import('@/constants/modules')).default;
          const global = new GlobalServices();

          // Fetch the project status list
          const statusOptions = await global.getUXDropDown(MODULE.PROJECTS.Status);
          // Fetched status options for board service initialization

          // Initialize the board service with status data
          const boardConfig = rootGetters['boardSettings/getBoardConfig']('jobs') || JOBS_BOARD_CONFIG;
          await boardService.initializeBoard(boardConfig, statusOptions);

          // Board service successfully initialized with status mappings
        } catch (error) {
          // Error initializing board service with statuses - continue without status initialization
          // Continue without status initialization if it fails
        }

        commit('SET_BOARD_SERVICE', boardService);
        
        // Load initial board data using current board settings
        await dispatch('loadBoardData');
        
        // Load filter options
        await dispatch('loadFilterOptions');
        
      } catch (error) {
        // Error initializing board - handled by UI notification
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async loadBoardData({ commit, state, getters, rootGetters }, { additionalFilters = null, forceRefresh = false } = {}) {
      try {
        commit('SET_LOADING', true);
        
        if (!state.boardService) {
          // If board service doesn't exist, create and initialize it
          const boardService = new BoardService();

          try {
            const GlobalServices = (await import('@/services/global')).default;
            const MODULE = (await import('@/constants/modules')).default;
            const global = new GlobalServices();

            // Fetch the project status list
            const statusOptions = await global.getUXDropDown(MODULE.PROJECTS.Status);
            // Initializing board service during loadBoardData

            // Initialize the board service with status data
            const boardConfig = rootGetters['boardSettings/getBoardConfig']('jobs') || JOBS_BOARD_CONFIG;
            await boardService.initializeBoard(boardConfig, statusOptions);
          } catch (error) {
            // Error initializing board service during loadBoardData - continuing with defaults
          }

          commit('SET_BOARD_SERVICE', boardService);
        }

        // Get current board configuration from board settings store
        const boardConfig = rootGetters['boardSettings/getBoardConfig']('jobs') || JOBS_BOARD_CONFIG;

        // Prepare filters for the API call
        const apiFilters = additionalFilters || getters.getApiFilters;
        
        // Load data using the board service with current settings
        // Pass forceRefresh to clear invoice status cache if refresh button was clicked
        const result = await state.boardService!.loadBoardData(boardConfig, apiFilters, forceRefresh);
        
        commit('SET_BOARD_COLUMNS', result.columns);
        commit('SET_TOTAL_JOBS', result.total);
        commit('UPDATE_LAST_REFRESH');
        
      } catch (error) {
        // Error loading board data - handled by UI notification
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async loadFilterOptions({ commit }) {
      try {
        // Load filter options using existing UX services
        const clientOptions = await global.getUXDropDown(MODULE.CLIENTS.ActionId);
        const projectsService = new UXProjectsService();
        const technicianService = new UXTechnicianService();
        const dueDateService = new UXDueDateService();
        
        const [projectTypes, technicians, dueDates, projectStatuses] = await Promise.all([
          projectsService.getProjectType(),
          technicianService.getTechnicians(),
          dueDateService.getDueDateList(),
          projectsService.getProjectStatus() // Add project status loading
        ]);

        commit('SET_FILTER_OPTIONS', {
          clients: clientOptions,
          jobTypes: projectTypes,
          assignees: technicians,
          dueDates: dueDates,
          projectStatuses: projectStatuses
        });
      } catch (error) {
        // Error loading filter options - using defaults
      }
    },

    async updateFilter({ commit, dispatch, state }, { filterType, value, objectValue }: { filterType: keyof BoardFilters, value: string | number, objectValue?: any }) {
      const updatedFilters = {
        ...state.filters,
        [filterType]: value
      };

      // Store the object value for display purposes
      if (objectValue !== undefined) {
        const objectKey = `${filterType}Object` as keyof BoardFilters;
        updatedFilters[objectKey] = objectValue;
      }

      commit('SET_FILTERS', updatedFilters);
      await dispatch('loadBoardData');
    },

    async applyFilters({ commit, dispatch }, filters: Partial<BoardFilters>) {
      commit('SET_FILTERS', filters);
      await dispatch('loadBoardData');
    },

    async resetFilters({ commit, dispatch }) {
      // Clear all filters and client-side state
      commit('CLEAR_FILTERS');

      // Load fresh data using only base filters (no additional filters)
      await dispatch('loadBoardData');
    },

    async searchBoard({ dispatch }, searchTerm: string) {
      const searchFilters = {
        // Map search term to appropriate filter fields
        jobNumber: searchTerm.includes('WO') ? searchTerm : '',
        address: !searchTerm.includes('WO') ? searchTerm : ''
      };
      
      await dispatch('applyFilters', searchFilters);
    },

    selectJob({ commit }, job: BoardJob) {
      commit('SET_SELECTED_JOB', job);
    },

    async updateJobStatus({ commit, state }, { jobId, newStatus, newStatusId, newColumnId }: { jobId: string, newStatus: string, newStatusId?: number, newColumnId: string }) {
      try {
        if (!state.boardService) {
          throw new Error('Board service not initialized');
        }

        // Find the job being moved to pass complete job data to the service
        let jobToMove: BoardJob | null = null;
        for (const column of state.boardColumns) {
          jobToMove = column.list.find(job => job.id === jobId) || null;
          if (jobToMove) break;
        }

        if (!jobToMove) {
          throw new Error('Job not found for status update');
        }

        // Update status via API - prefer statusId if available, fallback to status name
        if (newStatusId) {
          await state.boardService.updateJobStatusById(jobId, newStatusId, jobToMove);
        } else {
          await state.boardService.updateJobStatus(jobId, newStatus);
        }
        
        // Update local state
        commit('UPDATE_JOB_STATUS', { jobId, newStatus, newStatusId, newColumnId });
        
      } catch (error) {
        // Error updating job status - handled by UI notification
        throw error;
      }
    },

    animateJobMove({ commit }, { jobId, fromColumnId, toColumnId }: { jobId: string, fromColumnId: string, toColumnId: string }) {
      commit('ANIMATE_JOB_MOVE', { jobId, fromColumnId, toColumnId });
    },

    async refreshBoard({ dispatch }) {
      // Pass forceRefresh = true to clear invoice status cache and reload status excludes
      await dispatch('loadBoardData', { forceRefresh: true });
    },

    async applyClientSideJobNumberFilter({ commit }, extractedNumbers: string) {
      commit('SET_CLIENT_SIDE_JOB_FILTER', extractedNumbers);
    }
  },

  getters: {
    isLoading: (state) => state.isLoading,
    
    boardColumns: (state) => state.boardColumns,
    
    filteredBoardColumns: (state) => {
      // Apply client-side job number filtering for immediate UI feedback
      if (!state.clientSideJobFilter) {
        return state.boardColumns;
      }

      return state.boardColumns.map(column => ({
        ...column,
        list: column.list.filter(job => {
          // Extract numbers from the job ID/reference number
          const jobNumbers = job.id.replace(/[^0-9]/g, '');
          return jobNumbers.includes(state.clientSideJobFilter);
        })
      }));
    },
    
    getApiFilters: (state) => {
      // Convert board filters to dashboard API filter format
      const apiFilters: any = {};
      
      if (state.filters.client) {
        apiFilters.clientId = state.filters.client;
      }
      
      if (state.filters.jobNumber) {
        apiFilters.projectRefNumber = state.filters.jobNumber;
      }
      
      if (state.filters.jobType) {
        apiFilters.projectTypeId = state.filters.jobType;
      }
      
      if (state.filters.assignee) {
        apiFilters.technicianId = state.filters.assignee;
      }
      
      if (state.filters.address) {
        apiFilters.keywordAddress = state.filters.address;
      }
      
      if (state.filters.dueDate) {
        apiFilters.dueDateId = state.filters.dueDate;
      }
      
      return apiFilters;
    },
    
    totalJobsCount: (state, getters) => {
      return getters.filteredBoardColumns.reduce((total: number, col: BoardColumn) => total + col.list.length, 0);
    },

    jobCountsByStatus: (state, getters) => {
      const counts: { [key: string]: number } = {};
      getters.filteredBoardColumns.forEach((col: BoardColumn) => {
        counts[col.title] = col.list.length;
      });
      return counts;
    },

    getProjectStatusList: (state) => {
      return state.filterOptions.projectStatuses || [];
    },
    
    lastRefreshTime: (state) => state.lastRefresh,
    
    selectedJob: (state) => state.selectedJob,
    
    filterOptions: (state) => state.filterOptions,
    
    currentFilters: (state) => state.filters
  }
};

export default boardModule;
